import { RECARE_FILE_TYPE, SEARCH_ACTION_GENERATE_FORM } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getErrorMessage } from "core/model/utils/errors";
import { Auction, AuctionForm, TransitionalCareFormData } from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { scrollToTop } from "dsl/hooks/useScrollTo";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useCallback, useMemo } from "react";
import {
  SimpleFormRenderProps,
  composeValidation,
  convertEncryptedFieldIn,
  convertEncryptedFieldOut,
  validateModel,
} from "react-forms-state";
import { FormContainerState } from "react-forms-state/src/FormContainer";
import { useTranslations } from "translations";
import { ExitPreventionWrapper } from "../../utils";
import { TransitionalCareFormPresenter } from "./FormSections";
import { transitionalCareModelDefinition } from "./modelDefinition";
import {
  validatePatientConfirmation,
  validateTransitionalCareFormDates,
} from "./utils";

export const useGetTransitionalCareOnSubmit = ({
  auction,
}: {
  auction: Auction | undefined;
}) => {
  const translations = useTranslations();

  const [generateForm, generateFormProgress, resetProgress] = useSearchAction({
    actionType: SEARCH_ACTION_GENERATE_FORM,
  });
  const toast = useToast();
  const { isRedesignActive } = useIsDocsAndExtractActive();

  const onSubmit = useCallback(
    async (
      formData: TransitionalCareFormData,
      formState: FormContainerState,
    ) => {
      if (!formState.dirty || !auction) return;

      const updatedAuction = {
        ...auction,
        forms: [
          {
            form_data: convertEncryptedFieldOut(JSON.stringify(formData)),
            form_type: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
          },
        ],
      } as Auction;

      await generateForm({
        auction,
        context: {
          auction: updatedAuction,
          form_type: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
        },
        onCompleted: () => {
          if (!isRedesignActive) {
            toast({
              action: {
                label: translations.patientForms.transitionalCareForm.toTheTop,
                onClick: () => scrollToTop(),
              },
              color: "success",
              message:
                translations.patientForms.transitionalCareForm.pdfCreated,
            });
          }

          resetProgress();
        },
        onError: (err) => {
          console.error(
            `error saving transitional care form - ${getErrorMessage(err)}`,
          );
          toast({
            color: "danger",
            message: translations.auctionRequest.tryAgain,
          });
        },
      });
    },
    [convertEncryptedFieldOut, generateForm, auction],
  );

  return { onSubmit, onSubmitProgress: generateFormProgress };
};

export const getTransitionalCareFormData = ({
  auctionForm,
}: {
  auctionForm: Readonly<AuctionForm> | undefined;
}): TransitionalCareFormData | null => {
  if (!auctionForm?.form_data) return null;

  const stringified = convertEncryptedFieldIn(
    auctionForm.form_data as AnyObject,
  );

  if (stringified) {
    return JSON.parse(stringified) as TransitionalCareFormData;
  }

  return null;
};

const useGetFormInputValue = ({
  auctionForm,
}: {
  auctionForm: Readonly<AuctionForm> | undefined;
}): TransitionalCareFormData | null => {
  return useMemo(
    () => getTransitionalCareFormData({ auctionForm }),
    [auctionForm?.form_data],
  );
};

export function TransitionalCareForm({
  auction,
  auctionForm,
}: {
  auction: Auction;
  auctionForm: Readonly<AuctionForm> | undefined;
}) {
  const { onSubmit, onSubmitProgress } = useGetTransitionalCareOnSubmit({
    auction,
  });
  const formInputValue = useGetFormInputValue({ auctionForm });
  return (
    <SimpleFormRenderProps
      asHtmlForm
      formInputValue={formInputValue}
      modelDefinition={transitionalCareModelDefinition}
      validate={composeValidation(
        validateModel(transitionalCareModelDefinition),
        validatePatientConfirmation,
        validateTransitionalCareFormDates,
      )}
      onSubmit={onSubmit}
    >
      {({ dirty, onChange: formOnChange, submit }) => (
        <ExitPreventionWrapper isDirty={dirty}>
          <TransitionalCareFormPresenter
            auction={auction}
            formOnChange={formOnChange}
            hasPreviousFormValue={!!auctionForm}
            submit={submit}
            onSubmitProgress={onSubmitProgress}
          />
        </ExitPreventionWrapper>
      )}
    </SimpleFormRenderProps>
  );
}
